import styled, {CSSObject} from 'styled-components';
import styledMap from 'styled-map';
import {getColorFromTheme} from '../../ui-utils';
import {TChildProps} from '../../interface';
import {Headline4} from '../../atoms/typography/headline4';
import {memo, ReactNode} from 'react';

type CardKind = 'dark' | 'white';
type Padding = 'small' | 'normal' | 'large' | 'none';
type Display = 'none' | 'flex' | 'grid';

const Wrapper = styled.section<{variant?: string}>`
  border-width: 1px;
  border-style: solid;
  background-color: ${getColorFromTheme('white')};
  border-color: ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  padding: ${styledMap('variant', {
    exploreTemplates: '16px',
    default: '0px',
  })};
`;

const HeaderWrapper = styled.div<{
  padding?: Padding;
  display?: Display;
  variant?: string;
}>`
  width: 100%;
  padding: ${styledMap('padding', {
    small: '16px',
    normal: '24px',
    default: '16px 24px',
    none: '0px',
  })};
  display: ${styledMap('display', {
    flex: 'flex',
    grid: 'grid',
    default: 'flex',
    none: 'none',
  })};
  justify-content: space-between;
  align-items: center;
  flex-direction: row;

  @media (max-width: 500px) {
    flex-direction: ${styledMap('variant', {
      exploreTemplatesHeader: 'column',
      columnEnd: 'column',
      columnStart: 'column',
      default: 'row',
    })};

    align-items: ${styledMap('variant', {
      exploreTemplatesHeader: 'flex-end',
      columnStart: 'flex-start',
      columnEnd: 'flex-start',
      default: 'center',
    })};

    .action {
      margin-top: ${styledMap('variant', {
        exploreTemplatesHeader: '',
        columnStart: '',
        columnEnd: '20px',
        default: '',
      })};
      display: flex;

      width: ${styledMap('variant', {
        exploreTemplatesHeader: '',
        columnStart: '',
        columnEnd: '100%',
        default: '',
      })};

      justify-content: ${styledMap('variant', {
        exploreTemplatesHeader: '',
        columnStart: '',
        columnEnd: 'flex-end',
        default: '',
      })};
    }
  }
`;

const ContentWrapper = styled.div<{
  kind?: CardKind;
  padding?: Padding;
  variant?: string;
  hasFooter?: boolean;
}>`
  padding: ${styledMap('padding', {
    small: '4px',
    normal: '8px',
    large: '16px',
    default: '8px',
    none: '0px',
  })};
  width: 100%;
  box-shadow: 0px 0px 0px 1px ${getColorFromTheme('borderLight')};
  border-radius: ${(props) => {
    if (props.variant === 'exploreTemplates') return '0px';
    if (props.hasFooter) return '10px 10px 0px 0px';
    return '10px';
  }};
  background-color: ${({kind, theme}) =>
    kind === 'dark' ? theme.colors.backgroundDark : theme.colors.white};
`;

const FooterWrapper = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 0 0 10px 10px;
  background-color: ${getColorFromTheme('white')};
`;

export interface SectionCardProps extends TChildProps {
  title?: ReactNode | string;
  secondaryTitle?: ReactNode | string;
  action?: ReactNode;
  kind?: CardKind;
  headerPadding?: Padding;
  extraHeader?: ReactNode;
  variant?: string;
  contentPadding?: Padding;
  displayHeader?: Display;
  CustomHeaderTitle?: JSX.Element;
  contentStyling?: CSSObject;
  notificationheader?: ReactNode;
  footer?: ReactNode;
}

export const SectionCard = memo(function ({
  children,
  title,
  secondaryTitle,
  action,
  kind,
  headerPadding,
  variant,
  displayHeader,
  contentPadding,
  extraHeader,
  CustomHeaderTitle,
  contentStyling,
  notificationheader,
  footer,
}: SectionCardProps) {
  return (
    <Wrapper variant={variant} data-testid="section-card">
      {notificationheader}

      <HeaderWrapper
        className="headerClassName"
        padding={headerPadding}
        display={displayHeader}
        variant={variant}>
        {CustomHeaderTitle ? (
          CustomHeaderTitle
        ) : (
          <>{title && <Headline4>{title}</Headline4>}</>
        )}
        {secondaryTitle && <div style={{width: '175px'}}>{secondaryTitle}</div>}
        {action && <div className="action">{action}</div>}
      </HeaderWrapper>
      {extraHeader && extraHeader}
      <ContentWrapper
        padding={contentPadding}
        kind={kind}
        hasFooter={!!footer}
        style={contentStyling}>
        {children}
      </ContentWrapper>
      {footer && <FooterWrapper>{footer}</FooterWrapper>}
    </Wrapper>
  );
});
SectionCard.displayName = 'SectionCard';
